import React, { useCallback } from 'react';

import {
  SelectField,
  TextField,
  CheckboxesField,
  TextAreaField,
  ToggleSwitch,
  MCButton,
  View,
  FileBox,
  MCText,
  AsyncSelectField
} from 'src/components/basic';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import BaseComponent from 'src/components/BaseComponent';

import constants from 'src/constants';

import {
  lang,
  getSelectLabel,
  rangeToSelectList,
  remCss,
  scssVariables as variables,
  upperFirstChar,
  objectToSelectList,
  uniqueArray
} from 'src/helpers';
import HospitalSelect from 'src/components/complex/HospitalSelect';
import LANG_CONST from 'src/helpers/lang/const';
import { DropBox } from 'src/components/complex';
import AlertManager from 'src/components/alert/AlertManager';
import model from './model';

const FileMemorized = React.memo(({ file }) => {
  return (
    <View style={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <FileBox
        videoProps={{
          controls: false,
          playsinline: true,
          playing: false
        }}
        file={file}
        onClose={null}
        styles={{ width: remCss(140), height: remCss(140) }}
        onlyPreview={true}
        showFileName={true}
      />
    </View>
  );
});
FileMemorized.displayName = 'FileMemorized';

const createInitialFiles = (media = []) => {
  if (media.length) {
    return media.map((item, index) => ({ ...item, id: item.id || 'media_init_' + index }));
  }

  return [{ id: 'media_init_0', description: '' }];
};

const ChooseFileBox = ({ media, onChange }) => {
  const [files, setFiles] = React.useState(() => createInitialFiles(media));

  const onChangeHandle = useCallback(
    (cb) => {
      const changed = cb(files);
      onChange(changed);
      setFiles(changed);
    },
    [files, onChange]
  );

  return (
    <View style={{ position: 'relative', gap: remCss(20) }}>
      {files.map((item, index) => {
        return (
          <View style={{ flexDirection: 'row', gap: remCss(20) }} key={'mc-media-' + index}>
            {!item.file && !item.uri ? (
              <View style={{ width: 140 }}>
                <DropBox
                  onUpload={(n, files) => {
                    onChangeHandle((o) =>
                      o.map((a) => (a.id === item.id ? { ...a, file: files[0] } : a))
                    );
                  }}
                  style={{ flex: 1 }}
                  multiple={true}
                  labelIcon={LANG_CONST.choose_file}
                  type={'choose-file'}
                />
              </View>
            ) : (
              <FileMemorized file={item.file || item} />
            )}
            <div className={'mc-grow'}>
              <TextAreaField
                template={6}
                placeholder={LANG_CONST.description}
                name={'description'}
                value={item.description}
                onChange={(n, v) => {
                  onChangeHandle((o) =>
                    o.map((a) => (a.id === item.id ? { ...a, description: v } : a))
                  );
                }}
                invalid={{}}
              />
            </div>
            <View style={{ width: 50, alignItems: 'flex-start' }}>
              {index > 0 && (
                <MCButton
                  iconname={BASE_ICONS.DELETE}
                  light
                  radius
                  padding={5}
                  width={50}
                  secondary
                  onClick={() => {
                    onChangeHandle((o) => o.filter((a) => a.id !== item.id));
                  }}
                />
              )}
            </View>
          </View>
        );
      })}
      <View style={{ flexDirection: 'row' }}>
        <MCButton
          primary
          light
          medium
          radius
          style={{ width: 140 }}
          iconname={BASE_ICONS.PLUS}
          label={LANG_CONST.buttons.add_more}
          type={'button'}
          onClick={() => {
            if (files.length >= 10) {
              AlertManager.popup(AlertManager.TYPES.WARN, {
                close: false,
                title: lang.t(LANG_CONST.validation.media_limit, { limit: 10 }),
                actionButton: {
                  label: LANG_CONST.buttons.ok,
                  action: (actions) => {
                    actions.hide();
                  }
                }
              });
              return;
            }
            onChangeHandle((o) => [...o, { id: 'media_' + Date.now(), description: '' }]);
          }}
        />
      </View>
    </View>
  );
};

const apiStateId = 'create';

class Create extends BaseComponent {
  componentDidMount() {
    let { editItem, partnerId } = this.props;
    model.resetAddTreatment(editItem, partnerId);
    model.baseSubscribe(this.subscription);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  submit = (e) => {
    e.preventDefault();
    const { hide, afterSave } = this.props;
    model.create((error, data, success) => {
      if (afterSave) afterSave(error, data, success);
      if (!error && hide) hide();
    });
  };

  inputRecommendations = (index, name) => (n, v) => {
    const { handleChange } = model;
    const recommendations = model.input('recommendations');
    model.resetApiState(apiStateId);
    const old = [...recommendations];
    old[index][name] = v;
    handleChange('recommendations', [...old]);
  };

  inputTagRecommendations = (index, name) => (n, v) => {
    const { handleChange } = model;
    const recommendations = model.input('tagRecommendations');
    model.resetApiState(apiStateId);
    const old = [...recommendations];
    old[index][name] = v;
    handleChange('tagRecommendations', [...old]);
  };

  inputMedicine = (index, name) => (n, v) => {
    const { handleChange } = model;
    const medicines = model.input('medicines');
    model.resetApiState(apiStateId);
    const old = [...medicines];
    old[index][name] = ['times'].indexOf(name) > -1 ? n : v;
    handleChange('medicines', [...old]);
  };

  renderMedicine() {
    const createState = model.getApiState(apiStateId);
    const medicines = model.input('medicines');
    const { invalid: mainInvalid } = createState;
    const invalid = mainInvalid?.medicines;
    let html = [];

    for (let i = 0; i < medicines.length; i++) {
      const item = medicines[i];
      html.push(
        <div key={'medicine-item-' + i} className={'mc-flex-20 mc-flex-start'}>
          <div className={'mc-flex-col'} style={{ width: remCss(230) }}>
            <TextField
              name={'medicine_name_' + i}
              template={6}
              label={LANG_CONST.medicine}
              value={item.name}
              onChange={this.inputMedicine(i, 'name')}
              invalid={invalid}
            />
          </div>
          <div className={'mc-flex-col'} style={{ width: remCss(120) }}>
            <SelectField
              options={rangeToSelectList(1, 12)}
              template={6}
              label={LANG_CONST.doze}
              isClearable={false}
              name={'medicine_doze_' + i}
              value={item.doze}
              onChange={this.inputMedicine(i, 'doze')}
              invalid={invalid}
            />
          </div>
          <div className={'mc-flex-col'} style={{ width: remCss(120) }}>
            <SelectField
              label={LANG_CONST.days}
              options={rangeToSelectList(1, 12)}
              template={6}
              isClearable={false}
              name={'medicine_days_' + i}
              value={item.days}
              onChange={this.inputMedicine(i, 'days')}
              invalid={invalid}
            />
          </div>
          <div className={'mc-flex-col'} style={{ width: remCss(120) }}>
            <SelectField
              options={objectToSelectList(LANG_CONST.constants.medicine_period)}
              template={6}
              label={LANG_CONST.timing}
              isClearable={false}
              name={'medicine_period_' + i}
              value={item.period}
              onChange={this.inputMedicine(i, 'period')}
              invalid={invalid}
            />
            {invalid && invalid['medicine_times_' + i] && (
              <p className='mc-form-invalid-text' style={{ whiteSpace: 'pre' }}>
                {invalid['medicine_times_' + i]}
              </p>
            )}
          </div>
          <div className={'mc-grow mc-flex'} style={{ marginTop: remCss(35) }}>
            <CheckboxesField
              template={'2'}
              list={constants.general.medicine_times}
              data={item.times}
              onChange={this.inputMedicine(i, 'times')}
            />
            {i !== 0 ? (
              <button
                className={'mc-no-style-btn'}
                style={{ alignSelf: 'flex-start' }}
                type={'button'}
                onClick={() => model.delMedicine([i])}
              >
                <SVGIcon
                  iconname={BASE_ICONS.DELETE}
                  light
                  radius
                  padding={5}
                  width={50}
                  fill={variables['colors-mc-danger']}
                />
              </button>
            ) : (
              <div style={{ width: remCss(50), alignSelf: 'flex-start' }} />
            )}
          </div>
        </div>
      );
    }

    return html;
  }
  isValidTagRecommendationsQuery = (inputValue) => {
    // Check if input is not empty
    if (!inputValue || inputValue.trim() === '') {
      return false;
    }

    // Check minimum length
    if (inputValue.length < 3) {
      return false;
    }
    // Custom validation (e.g., no special characters)
    const regex = /^[a-zA-Z0-9\s]+$/;
    return regex.test(inputValue);
  };

  renderTagRecommendations() {
    const createState = model.getApiState(apiStateId);
    const recommendations = model.input('tagRecommendations');
    const { invalid: mainInvalid } = createState;
    const invalid = mainInvalid?.tagRecommendations || {};
    let html = [];
    for (let i = 0; i < recommendations.length; i++) {
      const item = recommendations[i];
      html.push(
        <div key={'recommendation-item-' + i} className={'mc-flex-20 mc-flex-start'}>
          <div className={'mc-flex-col mc-grow'} style={{ flex: 1 }}>
            <TextAreaField
              placeholder={LANG_CONST.txt}
              rows={5}
              name={'tag_recommendations_text_' + i}
              template={6}
              value={item.text}
              onChange={this.inputTagRecommendations(i, 'text')}
              invalid={invalid}
            />
            <AsyncSelectField
              allowNew={true}
              template={5}
              isClearable={true}
              apiConfig={{
                url: 'treatment/tags',
                labelKey: 'text'
              }}
              isMenuOpenFn={(query) => {
                return this.isValidTagRecommendationsQuery(query);
              }}
              isMultiple={true}
              name={'tags'}
              invalid={invalid}
              value={item?.tags || []}
              onChange={(n, v, option) => {
                this.inputTagRecommendations(i, 'tags')(
                  '',
                  uniqueArray([...(item?.tags || []), ...(option || []).map((o) => o.label)])
                );
              }}
              canProcess={this.isValidTagRecommendationsQuery}
              isValidNewOption={this.isValidTagRecommendationsQuery}
              placeholder={LANG_CONST.text.tag_placeholder}
              cacheOptions={false}
              blurInputOnSelect={false}
            />
            <div className={'mc-flex mc-wrap mc-flex-start'}>
              {[...(item?.tags || [])].map((tag) => (
                <MCButton
                  key={i + '-' + tag}
                  quaternary
                  small
                  rounded
                  light
                  style={{
                    paddingLeft: remCss(12),
                    paddingRight: remCss(8),
                    paddingTop: remCss(8),
                    paddingBottom: remCss(8)
                  }}
                >
                  {tag}
                  <MCButton
                    quaternary
                    iconname={BASE_ICONS.CLOSE}
                    width={22}
                    type={'button'}
                    onClick={() => {
                      const a = [...(item?.tags || [])].filter((old) => old !== tag);
                      this.inputTagRecommendations(i, 'tags')('', a);
                    }}
                    style={{ height: remCss(22) }}
                  />
                </MCButton>
              ))}
            </div>
          </div>
          {i !== 0 ? (
            <div className={'mc-flex-col'}>
              <button
                className={'mc-no-style-btn'}
                type={'button'}
                onClick={() => model.delTagRecommendations([i])}
              >
                <SVGIcon
                  iconname={BASE_ICONS.DELETE}
                  light
                  radius
                  padding={5}
                  width={50}
                  fill={variables['colors-mc-danger']}
                />
              </button>
            </div>
          ) : (
            <div style={{ width: remCss(50) }} />
          )}
        </div>
      );
    }
    return html;
  }
  render() {
    const { closeBtn, isPopup = true } = this.props;
    const { handleChange, handleBlur } = model;
    const { invalid, loading } = model.getApiState('create');
    const isEdit = this.props.editItem;

    const body = (
      <View className='mc-flex-20-col' onSubmit={this.submit}>
        <View style={{ flexDirection: 'row', gap: remCss(10) }}>
          <MCText size={20} weight={500} label={LANG_CONST.diagnosis} />
          <MCText size={18} textLight label={LANG_CONST.purpose_of_treatment} />
        </View>
        <TextField
          template={6}
          name={'diagnosis'}
          value={model.input('diagnosis')}
          onChange={(n, v) => {
            model.resetApiState(apiStateId);
            handleChange(n, v);
          }}
          placeholder={LANG_CONST.diagnosis}
          onBlur={handleBlur}
          invalid={invalid}
        />
        <MCText size={20} weight={500} label={LANG_CONST.description} />
        <TextAreaField
          template={6}
          placeholder={LANG_CONST.description}
          value={model.input('description')}
          onChange={(n, v) => {
            model.resetApiState(apiStateId);
            handleChange(n, v);
          }}
          onBlur={handleBlur}
          invalid={invalid}
          rows={5}
          name={'description'}
        />

        <MCText size={20} weight={500} label={LANG_CONST.recommendations} />
        <div className={'mc-flex-20-col'}>
          {this.renderTagRecommendations()}
          <div>
            <MCButton
              primary
              light
              medium
              radius
              iconname={BASE_ICONS.PLUS}
              label={LANG_CONST.buttons.add_new}
              type={'button'}
              onClick={model.addMoreTagRecommendations}
            />
          </div>
        </div>
        <MCText size={20} weight={500} label={LANG_CONST.prescribe_medicine} />
        <div className={'mc-flex-20-col'}>
          {this.renderMedicine()}
          <div>
            <MCButton
              primary
              light
              medium
              radius
              iconname={BASE_ICONS.PLUS}
              label={LANG_CONST.buttons.add_new}
              type={'button'}
              onClick={model.addMoreMedicine}
            />
          </div>
        </div>

        <div className={'mc-flex'}>
          <MCText size={20} weight={500} label={LANG_CONST.next_update} />
          <ToggleSwitch
            checked={model.state?.nextUpdateFlg}
            onChange={() => handleChange('nextUpdateFlg', !model.state?.nextUpdateFlg)}
          />
        </div>
        {model.state?.nextUpdateFlg && (
          <div className={'mc-flex-20'}>
            <TextField
              template={6}
              style={{ width: remCss(120) }}
              name={'nextUpdateAmount'}
              label={upperFirstChar(
                getSelectLabel(constants.general.time_unit, model.state?.nextUpdateUnit)
              )}
              value={model.input('nextUpdateAmount')}
              type={'number'}
              onChange={(n, v) => {
                model.resetApiState(apiStateId);
                handleChange(n, v);
              }}
              onBlur={handleBlur}
              invalid={invalid}
            />
            <SelectField
              width={200}
              template={6}
              name={'nextUpdateUnit'}
              options={constants.general.time_unit}
              value={model.input('nextUpdateUnit')}
              onChange={(n, v) => {
                model.resetApiState(apiStateId);
                handleChange(n, v);
              }}
              onBlur={handleBlur}
              invalid={invalid}
              label={LANG_CONST.type}
              isClearable={false}
            />
          </div>
        )}

        <MCText size={20} weight={500} label={LANG_CONST.clinic_location} />
        <div className={'mc-flex-20 mc-wrap'}>
          <HospitalSelect
            template={6}
            isClearable={false}
            placeholder={LANG_CONST.select}
            defaultValue={true}
            expandOptions={[{ id: '', hospital: lang.t(LANG_CONST.virtual) }]}
            apiConfig={{
              url: 'hospital/list',
              clinicIds: model.state.user?.clinicIds,
              hospitalIds: model.state.user?.hospitalIds
            }}
            name={'hospitalId'}
            invalid={invalid}
            value={model.input('hospitalId')}
            onChange={(n, v) => {
              model.resetApiState(apiStateId);
              handleChange(n, v);
            }}
          />
        </div>
        <div className={'mc-flex-col'}>
          <MCText size={20} weight={500} label={LANG_CONST.note} />
          <TextAreaField
            template={5}
            value={model.input('note')}
            onChange={(n, v) => {
              model.resetApiState(apiStateId);
              handleChange(n, v);
            }}
            onBlur={handleBlur}
            invalid={invalid}
            rows={5}
            name={'note'}
          />
        </div>
        <div className={'mc-flex-col'}>
          <MCText size={20} weight={500} label={LANG_CONST.upload_file} />
          <ChooseFileBox
            key={model.state.mediaId}
            media={model.state.media}
            multi={true}
            invalid={invalid}
            onChange={(v) => model.handleChange('media', v)}
          />
        </div>
      </View>
    );

    if (!isPopup) {
      return (
        <>
          {body}
          <div className='mc-flex'>
            <div className='mc-grow' />
            <MCButton
              primary
              dark
              radius
              medium
              disabled={loading}
              loading={loading}
              type={'submit'}
              onClick={this.submit}
              label={isEdit ? LANG_CONST.buttons.edit_treatment : LANG_CONST.buttons.add_treatment}
            />
          </div>
        </>
      );
    }

    return (
      <div className={'mc-popup-contain'} style={{ width: 'auto' }}>
        <div className='mc-modal-header'>
          <div className='mc-popup-title'>
            {lang.t(isEdit ? LANG_CONST.buttons.edit_treatment : LANG_CONST.buttons.add_treatment)}
          </div>
          {closeBtn}
        </div>
        <form className='mc-modal-body'>{body}</form>
        <div className='mc-modal-footer mc-flex'>
          <div className='mc-grow' />
          <MCButton
            primary
            light
            radius
            medium
            disabled={loading}
            type={'button'}
            onClick={this.props.hide}
            label={LANG_CONST.buttons.cancel}
          />
          <MCButton
            primary
            dark
            radius
            medium
            disabled={loading}
            loading={loading}
            type={'submit'}
            onClick={this.submit}
            label={isEdit ? LANG_CONST.buttons.edit_treatment : LANG_CONST.buttons.add_treatment}
          />
        </div>
      </div>
    );
  }
}

export default Create;
